import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  listPractitionersPage,
  type Practitioner,
} from "@/features/practitioners/api";
import { brand } from "@/config/brand";
import { openSession, selectOrg } from "@/features/eservices/api";

interface ConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected: () => void;
}

/**
 * The Connect → Access phased flow (legacy `es_connect` → `es_access`):
 * pick a CAF-holding practitioner, enter e-Services credentials (phase 1,
 * password held in memory only), then select the org/profile (phase 2). The
 * real TDS login is completed by the out-of-band connector.
 */
export function ConnectDialog({ open, onOpenChange, onConnected }: ConnectDialogProps) {
  const [cafHolders, setCafHolders] = useState<Practitioner[]>([]);
  const [phase, setPhase] = useState<1 | 2>(1);
  const [practitioner, setPractitioner] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [orgs, setOrgs] = useState<string[]>([]);
  const [org, setOrg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setPhase(1);
    setPassword("");
    setError("");
    listPractitionersPage({ pageSize: 100 })
      .then((res) => {
        const holders = res.practitioners.filter((p) => Boolean(p.cafNumber));
        setCafHolders(holders);
        const only = holders[0];
        if (holders.length === 1 && only) setPractitioner(only.id);
      })
      .catch(() => setCafHolders([]));
  }, [open]);

  const submitPhase1 = async () => {
    setError("");
    if (!practitioner) return setError("Select a practitioner.");
    if (!username || !password) return setError("Username and password are required.");
    setSubmitting(true);
    try {
      const result = await openSession({
        practitioner,
        esUsername: username,
        esPassword: password,
      });
      setPassword(""); // never keep the password around after the call
      setOrgs(result.orgs);
      setPhase(2);
      if (result.orgs.length === 1 && result.orgs[0]) setOrg(result.orgs[0]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitPhase2 = async () => {
    setError("");
    if (!org) return setError("Select an organization / profile.");
    setSubmitting(true);
    try {
      await selectOrg(practitioner, org);
      toast.success("e-Services session connected.");
      onConnected();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the profile.");
    } finally {
      setSubmitting(false);
    }
  };

  const noCafHolders = cafHolders.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect to IRS e-Services</DialogTitle>
          <DialogDescription>
            {phase === 1
              ? "Log in with a CAF-holding practitioner's e-Services credentials."
              : "Select the organization / profile returned by e-Services."}
          </DialogDescription>
        </DialogHeader>

        {noCafHolders ? (
          <Alert variant="destructive">
            <AlertTitle>No eligible practitioner</AlertTitle>
            <AlertDescription>
              You must configure a practitioner with a valid CAF number before
              connecting. Add one under Settings › Practitioners.
            </AlertDescription>
          </Alert>
        ) : phase === 1 ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="es-practitioner">Practitioner (CAF holder)</Label>
              <Select value={practitioner} onValueChange={setPractitioner}>
                <SelectTrigger id="es-practitioner">
                  <SelectValue placeholder="Select a practitioner" />
                </SelectTrigger>
                <SelectContent>
                  {cafHolders.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.fullName} · CAF {p.cafNumber}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="es-username">e-Services username</Label>
              <Input
                id="es-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="es-password">e-Services password</Label>
              <Input
                id="es-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="off"
              />
              <p className="text-muted-foreground text-xs">
                Held in memory for this session only — never stored.
              </p>
            </div>
            <Alert>
              <AlertTitle>Connector required</AlertTitle>
              <AlertDescription>
                The IRS login is completed by the {brand.name} connector (browser
                extension / desktop agent). Install it to finish connecting.
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="es-org">Organization / profile</Label>
            <Select value={org} onValueChange={setOrg}>
              <SelectTrigger id="es-org">
                <SelectValue placeholder="Select an organization" />
              </SelectTrigger>
              <SelectContent>
                {orgs.map((o) => (
                  <SelectItem key={o} value={o}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {orgs.length === 0 && (
              <p className="text-muted-foreground text-xs">
                No profiles returned yet — the connector posts them after login.
              </p>
            )}
          </div>
        )}

        {error && <p className="text-destructive text-sm">{error}</p>}

        <DialogFooter>
          {!noCafHolders && phase === 1 && (
            <Button onClick={submitPhase1} disabled={submitting}>
              {submitting ? "Connecting…" : "Continue"}
            </Button>
          )}
          {!noCafHolders && phase === 2 && (
            <Button onClick={submitPhase2} disabled={submitting}>
              {submitting ? "Saving…" : "Finish"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
